import { z } from 'zod';

import { ACCOUNT_EMAIL_MAX, ACCOUNT_NAME_MAX, ACCOUNT_PASSWORD_MAX, ACCOUNT_PASSWORD_MIN } from '@/lib/platform/accounts';
import { DESIGN_IDS, type DesignId } from '@/lib/storefront/designs';

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const createdAgencySchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  status: z.string(),
  host: z.string(),
});

export type CreatedAgency = z.infer<typeof createdAgencySchema>;

const HEX = /^#[0-9A-Fa-f]{6}$/;

export type NewAgencyInput = {
  slug: string;
  name: string;
  shortName: string;
  city: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  phone: string;
  whatsapp: string;
  email: string;
  rc: string;
  customDomain: string;
  design: DesignId;
  primaryColor: string;
  secondaryColor: string;
  ownerName: string;
  ownerEmail: string;
  ownerPassword: string;
  subscriptionTier: string;
};

export type AgencyStep = 'identity' | 'storefront' | 'plan' | 'owner';

export function parseCreatedAgency(payload: unknown): CreatedAgency {
  const parsed = createdAgencySchema.safeParse(payload);
  if (!parsed.success) throw new Error('The new agency could not be read.');
  return parsed.data;
}

export function provisionFieldError(input: NewAgencyInput): string | null {
  return provisionStepError('identity', input)
    ?? provisionStepError('storefront', input)
    ?? provisionStepError('plan', input)
    ?? provisionStepError('owner', input);
}

export function provisionStepError(step: AgencyStep, input: NewAgencyInput): string | null {
  switch (step) {
    case 'identity':
      return identityError(input);
    case 'storefront':
      return storefrontError(input);
    case 'plan':
      return SLUG.test(input.subscriptionTier) ? null : 'Choose a plan.';
    case 'owner':
      return ownerError(input);
    default: {
      const unknown: never = step;
      return unknown;
    }
  }
}

function identityError(input: NewAgencyInput): string | null {
  if (input.name.trim().length < 1 || input.name.trim().length > ACCOUNT_NAME_MAX) {
    return `Name must be between 1 and ${ACCOUNT_NAME_MAX} characters.`;
  }
  if (input.shortName.trim().length > 80) return 'Trade name must be 80 characters or fewer.';
  if (!SLUG.test(input.slug)) return 'Slug must be lowercase letters, numbers, and hyphens.';
  if (input.city.trim().length < 1 || input.city.trim().length > 80) return 'City must be between 1 and 80 characters.';
  if (input.address.trim().length < 1 || input.address.trim().length > 500) return 'Address must be between 1 and 500 characters.';
  if (input.phone.trim().length < 1 || input.phone.trim().length > 40) return 'Phone must be between 1 and 40 characters.';
  if (input.whatsapp.trim().length > 40) return 'WhatsApp must be 40 characters or fewer.';
  if (!validEmail(input.email)) return 'Enter a valid agency email address.';
  if (input.rc.trim().length > 40) return 'RC must be 40 characters or fewer.';
  if (input.customDomain.trim().length > 191) return 'Custom domain must be 191 characters or fewer.';
  if (input.customDomain.trim() !== '' && !input.customDomain.includes('.')) return 'Enter a hostname such as rentals.example.com.';
  return null;
}

function storefrontError(input: NewAgencyInput): string | null {
  if (!DESIGN_IDS.includes(input.design)) return 'Choose a storefront design.';
  if (!HEX.test(input.primaryColor) || !HEX.test(input.secondaryColor)) return 'Colours must be hex values such as #FF8A00.';
  return null;
}

function ownerError(input: NewAgencyInput): string | null {
  if (input.ownerName.trim().length < 1 || input.ownerName.trim().length > ACCOUNT_NAME_MAX) {
    return `Owner name must be between 1 and ${ACCOUNT_NAME_MAX} characters.`;
  }
  if (!validEmail(input.ownerEmail)) return 'Enter a valid owner email address.';
  if (input.ownerPassword.length < ACCOUNT_PASSWORD_MIN || input.ownerPassword.length > ACCOUNT_PASSWORD_MAX) {
    return `Password must be between ${ACCOUNT_PASSWORD_MIN} and ${ACCOUNT_PASSWORD_MAX} characters.`;
  }
  return null;
}

function validEmail(value: string): boolean {
  const email = value.trim();
  return email.length <= ACCOUNT_EMAIL_MAX && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
